import { api } from '../api.js';
import { subscribe } from '../state.js';
import { navigate } from '../router.js';
import { getTranslatedFields, getTranslatedEdgeLabel, translate } from '../lib/i18n.js';
import type { ApiConceptListItem, ApiRelationTypeTranslation } from '../../shared/types.js';

const PAGE_SIZE = 20;

interface RecentConcept extends ApiConceptListItem {
  type: 'concept';
  created_at: string;
}

interface RecentCrosslink {
  type: 'crosslink';
  created_at: string;
  source: { slug: string; title: string };
  target: { slug: string; title: string };
  relation_type: string | null;
  relation_type_translations?: ApiRelationTypeTranslation[];
  description?: string | null;
}

type RecentItem = RecentConcept | RecentCrosslink;

interface RecentResponse {
  items: RecentItem[];
  total: number;
  page: number;
  limit: number;
}

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr + 'Z').getTime()) / 1000);
  if (seconds < 60) return translate('recent.just_now', 'just now');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}

export async function renderRecentView(container: HTMLElement): Promise<void | (() => void)> {
  container.innerHTML = `<div class="loading">${translate('recent.loading', 'loading...')}</div>`;
  let langUnsub: (() => void) | null = null;
  let currentPage = 1;

  try {
    async function renderContent(): Promise<void> {
      const data = await api.getRecent(currentPage, PAGE_SIZE) as RecentResponse;
      const { items, total, page, limit } = data;
      const totalPages = Math.ceil(total / limit);

      container.innerHTML = '';
      const view = document.createElement('div');
      view.className = 'view view--recent';

      // Header
      const header = document.createElement('div');
      header.className = 'view__header';
      header.innerHTML = `
        <h1 class="view__title">${translate('recent.title', 'Recent')}</h1>
        <p class="view__subtitle">${translate('recent.subtitle', 'Latest additions to the network.')}</p>
      `;
      view.appendChild(header);

      // Timeline list
      const list = document.createElement('div');
      list.className = 'recent-list';

      for (const item of items) {
        const row = document.createElement('div');
        row.className = 'recent-item';

        if (item.type === 'concept') {
          const translated = getTranslatedFields(item);
          row.innerHTML = `
            <div class="recent-item__main">
              <div class="recent-item__row">
                <span class="recent-item__badge recent-item__badge--concept">${translate('recent.concept', 'concept')}</span>
                <span class="recent-item__label">${translated.title}</span>
                <span class="recent-item__time">${timeAgo(item.created_at)}</span>
              </div>
              ${translated.summary ? `<p class="recent-item__desc">${translated.summary}</p>` : ''}
            </div>
          `;
          row.style.cursor = 'pointer';
          row.addEventListener('click', () => navigate(`/concept/${item.slug}`));
        } else {
          const relLabel = getTranslatedEdgeLabel({
            label: item.relation_type,
            translations: item.relation_type_translations || [],
          });
          row.innerHTML = `
            <div class="recent-item__main">
              <div class="recent-item__row">
                <span class="recent-item__badge recent-item__badge--crosslink">${translate('recent.crosslink', 'crosslink')}</span>
                <span class="recent-item__label">
                  <a class="recent-item__link" data-slug="${item.source.slug}">${item.source.title}</a>
                  ${relLabel ? `<span class="recent-item__relation">${relLabel}</span>` : '<span class="recent-item__relation">&harr;</span>'}
                  <a class="recent-item__link" data-slug="${item.target.slug}">${item.target.title}</a>
                </span>
                <span class="recent-item__time">${timeAgo(item.created_at)}</span>
              </div>
              ${item.description ? `<p class="recent-item__desc">${item.description}</p>` : ''}
            </div>
          `;
          row.style.cursor = 'pointer';
          row.addEventListener('click', () => navigate(`/graph?edge=${item.source.slug},${item.target.slug}`));
          row.querySelectorAll('[data-slug]').forEach((link: Element) => {
            link.addEventListener('click', (e: Event) => {
              e.stopPropagation();
              navigate(`/concept/${(link as HTMLElement).dataset.slug}`);
            });
          });
        }

        list.appendChild(row);
      }

      if (items.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'recent-empty';
        empty.textContent = translate('recent.empty', 'Nothing here yet.');
        list.appendChild(empty);
      }

      view.appendChild(list);

      // Pagination
      if (totalPages > 1) {
        const pager = document.createElement('div');
        pager.className = 'recent-pager';

        const prevBtn = document.createElement('button');
        prevBtn.className = 'btn btn--ghost btn--small';
        prevBtn.textContent = translate('recent.prev', 'Prev');
        prevBtn.disabled = page <= 1;
        prevBtn.addEventListener('click', () => {
          if (currentPage > 1) { currentPage--; renderContent(); }
        });

        const info = document.createElement('span');
        info.className = 'recent-pager__info';
        info.textContent = `${page} / ${totalPages}`;

        const nextBtn = document.createElement('button');
        nextBtn.className = 'btn btn--ghost btn--small';
        nextBtn.textContent = translate('recent.next', 'Next');
        nextBtn.disabled = page >= totalPages;
        nextBtn.addEventListener('click', () => {
          if (currentPage < totalPages) { currentPage++; renderContent(); }
        });

        pager.appendChild(prevBtn);
        pager.appendChild(info);
        pager.appendChild(nextBtn);
        view.appendChild(pager);
      }

      container.appendChild(view);
    }

    await renderContent();
    langUnsub = subscribe('lang', () => renderContent());

    return () => { if (langUnsub) langUnsub(); };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error">${(err as Error).message}</p></div>`;
    return () => { if (langUnsub) langUnsub(); };
  }
}
